import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, shareReplay } from 'rxjs';
import { InviteTradesOptions } from 'src/app/job/models/job-detail/details/invite-trades-request.model';
import { InviteTradesResult } from 'src/app/job/models/job-detail/details/invite-trades-result.model';
import { NoteTagUserResult } from 'src/app/job/models/job-detail/Notes/notes.result.model';
import {
  AssignTradeOptions,
  AssignTradeResponse,
  MiniProfile,
  OnDemandTradesResponse,
} from 'src/app/job/models/job-post/assign-trade-request.model';
import { LicenceInsuaranceStatusResult } from 'src/app/job/models/job-post/licence-insurance-result.model';
import { TradePreference } from 'src/app/job/models/job-post/trade-preference.model';
import { PreferredTradeResponse } from 'src/app/job/models/preferred-trade-response.model';
import { EMPTY_GUID } from 'src/app/shared/constants/empty-guid-.constant';
import { TradeeProfileResult } from 'src/app/shared/models/trade-mgmt/trade.service.model';
import {
  GeneralProfile,
  ServiceArea,
  ServiceType,
} from 'src/app/tradee/models/tradee-general-profile.model';
import { ExternalTradeResult } from 'src/app/tradee/models/tradee.service.model';
import { environment } from 'src/environments/environment';
import { PaymentSettings } from '../../../../tradee/models/tradee-payment-settings.model';
import { BaseService } from '../base/base.service';
import { HomeERServiceTypeValidationResult } from 'src/app/job/models/job-post/homeer-service-type-validation-result.model';

@Injectable({
  providedIn: 'root',
})
export class TradeeService extends BaseService {
  private readonly _urlPrefix = `${environment.tradeMgmt.urlPrefix}/tradee`;

  constructor(http: HttpClient) {
    super(http);
  }

  public getProfile(id: string, organizationId: string) {
    return this.get<TradeeProfileResult>(
      `${this._urlPrefix}?id=${id}&organizationId=${organizationId}`
    );
  }

  public assignToAccount(data: {
    id: string;
    organizationId: string;
    accountCode: string;
  }) {
    return this.patch<string>(`${this._urlPrefix}/assign-to-account`, data);
  }

  getAssignTrades(pageIndex: number) {
    const req = AssignTradeOptions.getInstance();
    let params = new HttpParams();
    params = params.append('secondaryJobTypeId', `${req.secondaryJobTypeId}`);
    params = params.append('suburbId', `${req.suburbId}`);
    params = params.append('includeAllSuburbs', `${req.includeAllSuburbs}`);
    params = params.append(
      'includePendingInvitationTrades',
      `${req.includePendingInvitationTrades}`
    );
    params = params.append('bNATrades', `${req.selectBNATrades}`);
    params = params.append(
      'agencyPreferredTrades',
      `${req.selectAgencyPreferredTrades}`
    );
    params = params.append(
      'PMPreferredTrades',
      `${req.selectPMPreferredTrades}`
    );
    params = params.append('jobId', `${req.jobId}`);
    params = params.append('serachText', `${req.searchText}`);
    params = params.append('pageNumber', `${++pageIndex}`);
    return this.getNew<AssignTradeResponse[]>(
      `${this._urlPrefix}/assign-trades`,
      params
    ).pipe(
      map((trades) =>
        trades.map(
          (trade) =>
            ({
              ...trade,
              isSelected: false,
            } as AssignTradeResponse)
        )
      )
    );
  }

  tradeeMiniProfile(tradeeId: string) {
    if (tradeeId == EMPTY_GUID) {
      return of({});
    }
    return this.get<MiniProfile>(`${this._urlPrefix}/mini-profile/${tradeeId}`);
  }

  getOnDemandTrades(secondaryJobType: string, suburbId: string) {
    return this.get<OnDemandTradesResponse[]>(
      `${this._urlPrefix}/on-demand/${secondaryJobType}/${suburbId}`
    ).pipe(
      map((trades) =>
        trades.map(
          (trade) =>
            ({
              ...trade,
              isSelected: false,
            } as OnDemandTradesResponse)
        )
      )
    );
  }

  getTradeeGeneralProfile(tradeeId: string) {
    return this.get<GeneralProfile>(
      `${this._urlPrefix}/general-profile/${tradeeId}`
    );
  }

  getTradeeSuburbs(tradeeId: string) {
    return this.get<ServiceArea[]>(
      `${this._urlPrefix}/tradee-suburbs/${tradeeId}`
    );
  }

  getTradeeServiceTypes(tradeeId: string) {
    return this.get<ServiceType[]>(
      `${this._urlPrefix}/tradee-service-types/${tradeeId}`
    );
  }

  checkTradeExistence(email: string, companyName: string) {
    let endpointUrl = `${this._urlPrefix}/is-registered?Email=${email}&CompanyName=${companyName}`;
    return this.get<any>(endpointUrl);
  }
  hasUnregisteredTrades(tradeIds: string[]) {
    let endpointUrl = `${this._urlPrefix}/has-unregistered-trades`;
    return this.post<boolean>(endpointUrl, tradeIds);
  }

  validateLicenceInsurance(primaryJobTypeId: string, tradeIdList: string[]) {
    var params = this.queryParams({
      PrimaryJobTypeId: primaryJobTypeId,
      TradeId: tradeIdList,
    });
    let endpointUrl = `${this._urlPrefix}/validate-licence-insurance${params}`;
    return this.get<LicenceInsuaranceStatusResult>(endpointUrl);
  }

  getTradeePaymentSettings(tradeeId: string) {
    return this.get<PaymentSettings[]>(
      `${this._urlPrefix}/tradee-payment-settings/${tradeeId}`
    );
  }

  searchExternalTrades(searchTerm: string) {
    return this.get<ExternalTradeResult[]>(
      `${this._urlPrefix}/search/external?searchTerm=` + searchTerm
    );
  }

  getAssignAllTrades(pageIndex: number) {
    const req = AssignTradeOptions.getInstance();
    let params = new HttpParams();
    params = params.append('jobId', `${req.jobId}`);
    params = params.append(
      'agencyPreferredTrades',
      `${req.selectAgencyPreferredTrades}`
    );
    params = params.append(
      'PMPreferredTrades',
      `${req.selectPMPreferredTrades}`
    );
    params = params.append('serachText', `${req.searchText}`);
    params = params.append('pageNumber', `${++pageIndex}`);
    params = params.append('suburbId', `${req.suburbId}`);
    return this.getNew<AssignTradeResponse[]>(
      `${this._urlPrefix}/assign-all-trades`,
      params
    ).pipe(
      map((trades) =>
        trades.map(
          (trade) =>
            ({
              ...trade,
              isSelected: false,
            } as AssignTradeResponse)
        )
      )
    );
  }

  tradeList$: Observable<AssignTradeResponse[]>;
  getAssignAllTradesWithoutPaginations(isFromRule: boolean ) {
    if (!this.tradeList$) {
      let params = new HttpParams();
      params = params.append('isFromRule', `${isFromRule}`);
      this.tradeList$ = this.getNew<AssignTradeResponse[]>(
        `${this._urlPrefix}/assign-all-trades`,
        params
      )
        .pipe(
          map((trades) =>
            trades.map(
              (trade) =>
                ({
                  ...trade,
                  isSelected: false,
                } as AssignTradeResponse)
            )
          )
        )
        .pipe(shareReplay(1));
    }

    return this.tradeList$;
  }

  saveExternalTradee(data: { name: string; email: string }) {
    return this.put<string>(`${this._urlPrefix}/external`, data);
  }

  updatePreferences(data: TradePreference) {
    return this.put<PreferredTradeResponse>(
      `${this._urlPrefix}/update-agency-and-pm-preferred-trades`,
      data
    );
  }

  getNoteTagTrades(searchTerm: string) {
    const queryParams = this.queryParams({
      searchTerm,
    });
    return this.get<NoteTagUserResult[]>(
      `${this._urlPrefix}/search-by-name${queryParams}`
    );
  }

  addServiceTypeForFurureJobs(data: {
    tradeId: string;
    primaryServiceType: string;
    SecondaryServiceType: string;
  }) {
    const req = AssignTradeOptions.getInstance();
    var requestData = {
      tradeId: data.tradeId,
      primaryServiceType: data.primaryServiceType,
      SecondaryServiceType: data.SecondaryServiceType,
      secondaryJobTypeId: req.secondaryJobTypeId,
    };

    return this.post<string>(
      `${this._urlPrefix}/add-servicetype-to-future-jobs`,
      requestData
    );
  }

  getTradesToInviteForJob(pageIndex: number) {
    const req = InviteTradesOptions.getInstance();
    let params = new HttpParams();
    params = params.append('secondaryJobTypeId', `${req.secondaryJobTypeId}`);
    params = params.append('suburbId', `${req.suburbId}`);
    params = params.append('includeAllSuburbs', `${req.includeAllSuburbs}`);
    params = params.append(
      'includePendingInvitationTrades',
      `${req.includePendingInvitationTrades}`
    );
    params = params.append('bnATrades', `${req.bnATrades}`);
    params = params.append('jobId', `${req.jobId}`);
    params = params.append('searchText', `${req.searchText}`);
    params = params.append('pageNumber', `${++pageIndex}`);
    return this.getNew<InviteTradesResult[]>(
      `${this._urlPrefix}/invite-trades`,
      params
    ).pipe(
      map((trades) =>
        trades.map(
          (trade) =>
            ({
              ...trade,
              isSelected: false,
            } as InviteTradesResult)
        )
      )
    );
  }

  validateHomeERServiceType(primaryJobTypeId: string) {
    var params = this.queryParams({
      PrimaryJobTypeId: primaryJobTypeId
    });
    let endpointUrl = `${this._urlPrefix}/validate-home-er-services${params}`;
    return this.get<HomeERServiceTypeValidationResult>(endpointUrl);
  }
}
